import React, { useId, PropsWithChildren, ReactNode } from "react";
import cx from "classnames";
import DOMPurify from "dompurify";
import { Tooltip as ReactTooltip, TooltipRefProps } from "react-tooltip";
import { ReactComponent as CloseIcon } from "component-library/src/images/svg/icons/cross-thin.svg";
import "./styles.scss";
import { useIsMobile } from "../../../generics/dom-extensions";
import { dataTestIds } from "data-testids";

export interface TooltipProps {
  title?: string;
  html?: string;
  content?: ReactNode;
  datatestid?: string;
  disabled?: boolean;
  tooltipClassName?: string;
  className?: string;
}

const Tooltip = ({
  title,
  html,
  content,
  children,
  disabled,
  datatestid,
  className,
  tooltipClassName,
}: PropsWithChildren<TooltipProps>) => {
  const id = useId();
  const isMobile = useIsMobile();
  const tooltipRef = React.useRef<TooltipRefProps>(null);

  if (disabled) return <>{children}</>;

  return (
    <>
      {children && (
        <span
          data-tooltip-id={id}
          className={className}
          data-testid={dataTestIds.componentLibrary["Molecules.Tooltip.Icon"]}
        >
          {children}
        </span>
      )}
      <ReactTooltip
        id={id}
        clickable
        className={cx("tooltip", tooltipClassName)}
        border="2px solid #b2b2b2"
        arrowSize={20}
        offset={20}
        opacity={1}
        openOnClick={isMobile}
        globalCloseEvents={{
          escape: true,
          scroll: true,
          clickOutsideAnchor: true,
        }}
        ref={tooltipRef}
      >
        <div className="flex gap-1" data-testid={datatestid}>
          <div>
            {title && <p className="font-semibold mt-1">{title}</p>}
            {html ? (
              <p
                className="mt-1 break-all whitespace-pre-line font-normal"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
              />
            ) : (
              <p className="mt-1 break-all whitespace-pre-line font-normal">
                {content}
              </p>
            )}
          </div>
          <div className="ml-auto w-auto">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                tooltipRef.current?.close();
              }}
            >
              <CloseIcon
                width={25}
                height={25}
                className="pointer-events-none"
              />
            </button>
          </div>
        </div>
      </ReactTooltip>
    </>
  );
};

export default Tooltip;
